<template>
  <div class="bill-view page--bill-view">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{
            $t('bill-view.page.title', {
              billId: bill.partnerOrderName,
              createdAt: bill.createdAtDate,
            })
          }}
        </div>
      </div>
    </div>
    <div class="bill">
      <template v-if="isBillLoaded">
        <component
          :is="detailsComponent"
          :bill="bill"
          class="bill__info"
          @linkCopied="handleLinkCopied"
        ></component>
        <div class="bill__actions">
          <require-access
            :data="detailsActions.mainAction.accessControl.data || null"
            :action="detailsActions.mainAction.accessControl.action || null"
            v-if="detailsActions.mainAction"
          >
            <primary-button
              class="bill__actions-item"
              :label="detailsActions.mainAction.label"
              @click="detailsActions.mainAction.handler"
            ></primary-button>
          </require-access>
          <require-access
            :data="detailsActions.secondaryAction.accessControl.data || null"
            :action="
              detailsActions.secondaryAction.accessControl.action || null
            "
            v-if="detailsActions.secondaryAction"
          >
            <no-bg-button
              class="bill__actions-item"
              :icon="detailsActions.secondaryAction.icon"
              :label="detailsActions.secondaryAction.label"
              @click="detailsActions.secondaryAction.handler"
            ></no-bg-button>
          </require-access>
        </div>
      </template>
      <template v-else>
        <div class="bill__loading">
          <loader></loader>
        </div>
      </template>
    </div>

    <!--  POPUP AND NOTIFICATIONS  -->
    <bill-receipt-popup
      :bill="bill"
      :pdf-blob="billReceiptBlob"
      ref="billReceiptPopup"
    ></bill-receipt-popup>
    <share-bill-link-popup
      :bill="bill"
      ref="shareBillLinkPopup"
    ></share-bill-link-popup>
    <success-notification
      ref="billLinkCopiedNotification"
      :text="$t('bills.popup.share-bill-link.notifications.copied')"
    ></success-notification>
  </div>
</template>

<script>
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import NoBgButton from '@vue-app/components/base/button/NoBgButton.vue'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import BillCreatedDetails from '@vue-app/components/company/bills/details/BillCreatedDetails.vue'
import BillPaidDetails from '@vue-app/components/company/bills/details/BillPaidDetails.vue'
import BillCanceledDetails from '@vue-app/components/company/bills/details/BillCanceledDetails.vue'
import BillRefundDetails from '@vue-app/components/company/bills/details/BillRefundDetails.vue'
import ShareBillLinkPopup from '@vue-app/components/company/bills/popup/ShareBillLinkPopup.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import { Bill } from '@js-app/entities/Bill'
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'

import BillReceiptPopup from '@vue-app/components/company/bills/popup/BillReceiptPopup.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'

export default {
  name: 'BillView',
  components: {
    RequireAccess,
    Loader,
    BillReceiptPopup,
    ArrowButton,
    SuccessNotification,
    ShareBillLinkPopup,
    NoBgButton,
    PrimaryButton,
    BillCreatedDetails,
    BillPaidDetails,
    BillCanceledDetails,
    BillRefundDetails,
  },
  props: ['id', 'billId'],
  data() {
    return {
      bill: new Bill(),
      billReceiptBlob: null,
      isBillLoaded: false,
    }
  },
  created() {
    this.loadBill()
  },
  methods: {
    async loadBill() {
      this.isBillLoaded = false
      let _billResponse = await CompaniesApi.getBill(this.id, this.billId)
      this.bill = new Bill(_billResponse.data.ReportOperations[0])
      this.isBillLoaded = true
    },

    toBillsPage() {
      this.$router.push(
        RouteHelper.getRoute('bills-list', { companyId: this.id })
      )
    },

    shareBill() {
      //TODO: Make share bill popup
      this.$refs.shareBillLinkPopup.open(this.bill)
    },

    async openReceipt() {
      let receiptResponse = await CompaniesApi.getBillReceipt(
        this.id,
        this.bill.operationId
      )
      this.billReceiptBlob = URL.createObjectURL(receiptResponse.data)

      this.$refs.billReceiptPopup.open()
    },

    makeRefund() {
      this.$router.push(
        RouteHelper.getRoute('bill-refund', {
          companyId: this.id,
          billId: this.bill.operationId,
        })
      )
    },

    repeatBill() {
      //TODO: Make repeat bill handler
      alert('Repeat bill handler')
    },

    toRefundHistory() {
      //TODO: Make to refund history handler
      alert('To refund history handler')
    },

    handleLinkCopied() {
      this.$refs.billLinkCopiedNotification.show()
    },
  },
  computed: {
    backLinkRoute() {
      return RouteHelper.getRoute('bills-list', { companyId: this.id })
    },

    detailsComponent() {
      switch (this.bill.operationStatus.toString()) {
        case '0':
          return 'BillCreatedDetails'
        case '3':
          return 'BillPaidDetails'
        case '5':
          return 'BillCanceledDetails'
        case 'refund':
          return 'BillRefundDetails'
        default:
          return 'BillCreatedDetails'
      }
    },

    detailsActions() {
      let mainAction = null
      let secondaryAction = null

      switch (this.bill.operationStatus.toString()) {
        case '3': {
          mainAction = {
            label: this.$t('bill-view.bill.open-receipt'),
            handler: this.openReceipt,
            accessControl: {},
          }
          if (this.bill.canRefund) {
            secondaryAction = {
              label: this.$t('bill-view.bill.make-refund'),
              handler: this.makeRefund,
              icon: `<svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 5.125C12.1926 2.72955 9.88002 1 7.15321 1C3.75488 1 1 3.68629 1 7C1 10.3137 3.75488 13 7.15321 13C8.59045 13 9.9126 12.5195 10.9599 11.7143" stroke="#64748B" stroke-width="2" stroke-linecap="round"/><path d="M9.99979 4.85713L12.4636 5.7811C12.9917 5.97915 13.579 5.70155 13.7611 5.16778L14.566 2.80937" stroke="#64748B" stroke-width="2" stroke-linecap="round"/></svg>`,
              accessControl: {
                data: 'bills-refund',
                action: 'read',
              },
            }
          }

          break
        }
        case '5':
          mainAction = {
            label: this.$t('bill-view.bill.open-receipt'),
            handler: this.repeatBill,
            accessControl: {},
          }
          break
        case 'refund': {
          if (this.bill.canRefund) {
            mainAction = {
              label: this.$t('bill-view.bill.open-receipt'),
              handler: this.openReceipt,
              accessControl: {},
            }
          }
          secondaryAction = {
            label: this.$t('bill-view.bill.to-refund-history'),
            handler: this.toRefundHistory,
            icon: `<svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 5.125C12.1926 2.72955 9.88002 1 7.15321 1C3.75488 1 1 3.68629 1 7C1 10.3137 3.75488 13 7.15321 13C8.59045 13 9.9126 12.5195 10.9599 11.7143" stroke="#64748B" stroke-width="2" stroke-linecap="round"/><path d="M9.99979 4.85713L12.4636 5.7811C12.9917 5.97915 13.579 5.70155 13.7611 5.16778L14.566 2.80937" stroke="#64748B" stroke-width="2" stroke-linecap="round"/></svg>`,
            accessControl: {},
          }
          break
        }
        case '0':
        default:
          mainAction = {
            label: this.$t('bill-view.bill.share'),
            handler: this.shareBill,
            accessControl: {
              data: 'bills-share',
              action: 'read',
            },
          }
          secondaryAction = {
            label: this.$t('bill-view.bill.back-to-history'),
            handler: this.toBillsPage,
            icon: null,
            accessControl: {},
          }
      }

      return { mainAction, secondaryAction }
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--bill-view {
    padding-top: 31px;
  }
}
.bill {
  width: 100%;
  max-width: 615px;
  margin-top: 27px;
  background: #ffffff;
  border-radius: 22px;
  padding: 29px 27px 38px;
  &__loading {
    padding: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__header {
    display: flex;
    align-items: center;
  }

  &__number {
    margin-left: 27px;
    margin-right: 27px;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    color: #666d79;
  }

  &__lang {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #585f6d;
    margin-left: auto;
    margin-right: 30px;
  }

  &__status {
    width: 85px;
  }

  &__info {
    margin-top: 25px;
  }

  &__actions {
    margin-top: 43px;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-item {
      width: 100%;
      max-width: 290px;
      padding: 25px;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
